import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { ZkIOption } from '@zenklub/ng-ui/component/input/dropdown/dropdown.interface';

import { ApiErrorResponse } from '../../../interfaces/error.interface';
import {
	IProfessionalEducation,
	IProfessionalEducationType,
} from '../../../interfaces/professionalprofile.interface';
import { ErrorService } from '../../../services/error.service';
import { GlobalService } from '../../../services/global.service';
import { UserService } from '../../../services/user.service';

@Component({
	selector: 'app-my-account-professional-education',
	templateUrl: './professional-education.component.html',
	styleUrls: ['./professional-education.component.scss'],
})
export class ProfessionalEducationComponent implements OnInit {
	isMobile = this.global.isMobileDevice();

	isDialogEducationOpen = false;
	isDialogEducationRemoveOpen = false;
	isEducationFormShown = false;
	isSaving: boolean;
	formGroupEducation: FormGroup;
	educationCurrentItem: IProfessionalEducation;
	educationTypeOptions: ZkIOption[] = [];
	educationFinishedInOptions: ZkIOption[] = [];

	@Input() educations: IProfessionalEducation[] = [];

	constructor(
		private errorService: ErrorService,
		public user: UserService,
		private global: GlobalService,
		public notifications: ZkNotificationService,
		private translate: TranslateService
	) {}

	ngOnInit() {
		this.buildFormEducationOptions();
		this.initFormEducation();
	}

	addEducation() {
		this.educationCurrentItem = undefined;
		this.formGroupEducation.reset();
		this.formGroupEducation.markAsPristine();
		this.isDialogEducationOpen = true;
		this.isEducationFormShown = true;
	}

	editEducation(education: IProfessionalEducation) {
		this.educationCurrentItem = education;
		this.setFormValues(education);
		this.isDialogEducationOpen = true;
		this.isEducationFormShown = true;
	}

	removeEducation() {
		if (this.educationCurrentItem && !this.isSaving) {
			const itemIndex = this.educations.indexOf(this.educationCurrentItem);
			this.educations.splice(itemIndex, 1);
			this.isSaving = true;
			const data = { education: this.getEducationsToSave() };
			this.user.updateProfileData(data, '/education').subscribe(
				() => {
					this.isSaving = false;
					this.educationCurrentItem = undefined;
					this.closeModalEducation();
				},
				(error: ApiErrorResponse) => {
					this.educations.splice(itemIndex, 0, this.educationCurrentItem);
					this.onRequestError(error);
				}
			);
		}
	}

	onRequestRemoveEducation(education: IProfessionalEducation) {
		this.educationCurrentItem = education;
		this.isDialogEducationRemoveOpen = true;
	}

	isFieldInvalid(formGroup: FormGroup, fieldName: string): boolean {
		const field = formGroup.get(fieldName);
		if (field) {
			return field.invalid && (field.dirty || field.touched);
		}
		return false;
	}

	closeModalEducation() {
		if (this.isSaving) return;
		this.isDialogEducationOpen = false;
		this.isDialogEducationRemoveOpen = false;
		this.formGroupEducation.reset();

		setTimeout(() => {
			this.isEducationFormShown = false;
		}, 300);
	}

	saveEducation() {
		if (!this.formGroupEducation.valid || this.isSaving) {
			this.formGroupEducation.markAllAsTouched();
			return;
		}
		this.isSaving = true;
		const education: IProfessionalEducation = this.formGroupEducation.value;
		education.educationYear = Number(education.educationYear);
		if (this.isModalActionAdd()) {
			this.educations.push(education);
		} else {
			this.educationCurrentItem = Object.assign(
				this.educationCurrentItem,
				education
			);
		}
		const data = { education: this.getEducationsToSave() };
		this.user.updateProfileData(data, '/education').subscribe(
			() => {
				this.isSaving = false;
				this.educationCurrentItem = undefined;
				this.closeModalEducation();
			},
			(error: ApiErrorResponse) => {
				if (this.isModalActionAdd()) {
					this.educations.splice(this.educations.indexOf(education), 1);
				}
				this.onRequestError(error);
			}
		);
	}

	sortEducations(educations): IProfessionalEducation[] {
		const dynamicSort = (property) => {
			let sortOrder = 1;

			if (property[0] === '-') {
				sortOrder = -1;
				property = property.substr(1);
			}

			return (a, b) => {
				const result =
					a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
				return result * sortOrder;
			};
		};

		return educations.sort(dynamicSort('-educationYear'));
	}

	translateEducationType(educationType: string): string {
		return this.translate.instant(
			`MY_ACCOUNT.PROFESSIONAL.FORM.EDUCATION.TYPE.${educationType}`
		);
	}

	onRequestError(error: ApiErrorResponse) {
		this.isSaving = false;
		this.notifications.showError(
			this.errorService.getMessage(error.error),
			this.translate.instant(
				'MY_ACCOUNT.PROFESSIONAL.MODAL.EDUCATION.ERRORS.GENERIC_TITLE'
			)
		);
	}

	private getEducationsToSave(): IProfessionalEducation[] {
		return this.educations.map((education) => {
			return {
				educationYear: education.educationYear,
				institution: education.institution || null,
				title: education.title,
				educationType: education.educationType,
			};
		});
	}

	private isModalActionAdd(): boolean {
		return !this.educationCurrentItem;
	}

	private setFormValues(professionalEducation: IProfessionalEducation) {
		this.formGroupEducation.patchValue({
			professionalId: professionalEducation.professionalId,
			educationType: professionalEducation.educationType,
			title: professionalEducation.title,
			institution: professionalEducation.institution,
			educationYear: professionalEducation.educationYear?.toString() || '',
		});
	}

	private initFormEducation() {
		this.formGroupEducation = new FormGroup({
			professionalId: new FormControl(''),
			educationType: new FormControl('', Validators.required),
			title: new FormControl('', Validators.required),
			institution: new FormControl('', Validators.required),
			educationYear: new FormControl('', Validators.required),
		});
	}

	private buildFormEducationOptions() {
		const educationTypeOptions: IProfessionalEducationType[] = [
			'degree',
			'master',
			'doctorate',
			'phd-mba',
			'other',
			'course',
		];

		educationTypeOptions.forEach((typeOption) => {
			this.educationTypeOptions.push({
				label: this.translate.instant(
					`MY_ACCOUNT.PROFESSIONAL.MODAL.EDUCATION.TYPE.OPTIONS.${typeOption}`
				),
				value: typeOption,
			});
		});
		const maxEducationYear = new Date().getFullYear();
		Array.from(Array(101).keys()).forEach((number) => {
			this.educationFinishedInOptions.push({
				label: (maxEducationYear - number).toString(),
				value: (maxEducationYear - number).toString(),
			});
		});
	}
}
